import {
  Camera,
  Pencil,
  Copy,
  Move,
  HelpCircle,
  Trash2,
  Share2,
  PanelTopClose,
  PanelTop,
  Rewind,
  Sparkles,
  Grid2x2,
  Grid2x2X,
  RotateCcw,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface PanelMoreMenuProps {
  onDelete: () => void
  /**
   * Open this panel's own settings. Rendered FIRST (a component-specific
   * action per the menu convention). Undefined hides the item — for panels
   * that carry a dedicated settings control in their header instead.
   */
  onEdit?: () => void
  onExport?: () => void
  onDuplicate?: () => void
  /** Relocate this panel via the placement overlay (split, tab, or replace).
   *  Undefined hides the item, e.g. the only panel in a layout. */
  onMove?: () => void
  /** Label for the Move item: "Move tab" when the pane hosts tabs. */
  moveLabel?: string
  /** When true, hide destructive/structural actions (Delete). */
  locked?: boolean
  /** Callback to toggle the panel header's visibility (headerVisible in WindowConfig). */
  onToggleHeaderVisible?: () => void
  /** Current headerVisible state — determines "Hide top bar" vs "Show top bar" label. */
  headerVisible?: boolean
  /** Chart panels: download a PNG of the chart (routes to the panel's
   *  registered 'screenshot' action). */
  onScreenshot?: () => void
  /** Single-chart panels: start bar replay (routes to the
   *  panel's registered 'bar-replay' action). Undefined hides the item. */
  onBarReplay?: () => void
  /** Open the chat drawer with this panel already added as a mention. Undefined hides the item. */
  onAskAI?: () => void
  /** Stats panels: toggle the stat-card row above the aggregates table.
   *  Undefined hides the item. */
  onToggleCards?: () => void
  /** Current card-row visibility; picks "Hide cards" vs "Show cards". */
  cardsVisible?: boolean
  /** Put this panel back to its default (component-specific, first section). Undefined hides it. */
  onReset?: () => void
  /** The reset item's label, e.g. "Reset to default window". */
  resetLabel?: string
}

export function PanelMoreMenu({
  onDelete,
  onEdit,
  onExport,
  onDuplicate,
  onMove,
  moveLabel = 'Move',
  locked,
  onToggleHeaderVisible,
  headerVisible = true,
  onScreenshot,
  onBarReplay,
  onAskAI,
  onToggleCards,
  cardsVisible = false,
  onReset,
  resetLabel,
}: PanelMoreMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title="More"
          data-companion-label="panel-more-menu"
          onMouseDown={(e) => e.stopPropagation()}
          className="flex h-6 w-6 items-center justify-center rounded border-none bg-transparent text-muted-foreground hover:bg-hover hover:text-accent-foreground"
          style={{ padding: 0, cursor: 'pointer' }}
        >
          <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor">
            <circle cx={12} cy={5} r={1.5} />
            <circle cx={12} cy={12} r={1.5} />
            <circle cx={12} cy={19} r={1.5} />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Menu convention (applies to every panel dropdown, decided
            2026-08-20): component-specific actions FIRST, then panel/layout
            actions (Duplicate, Hide top bar), then general actions (Share,
            Ask AI, Help), then destructive (Delete, red) last. Sections are
            separator-divided; a section with no items renders nothing (no
            dangling separator). New component-specific items go in the top
            section; new general items go with Share/Ask AI/Help. */}

        {/* Section 1: component-specific actions */}
        {onEdit && (
          <DropdownMenuItem onClick={onEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
        )}
        {onReset && (
          <DropdownMenuItem onClick={onReset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            {resetLabel}
          </DropdownMenuItem>
        )}
        {onScreenshot && (
          <DropdownMenuItem onClick={onScreenshot}>
            <Camera className="mr-2 h-4 w-4" />
            Screenshot
          </DropdownMenuItem>
        )}
        {onBarReplay && (
          <DropdownMenuItem onClick={onBarReplay}>
            <Rewind className="mr-2 h-4 w-4" />
            Bar replay…
          </DropdownMenuItem>
        )}
        {onToggleCards && (
          <DropdownMenuItem onClick={onToggleCards}>
            {cardsVisible ? (
              <Grid2x2X className="mr-2 h-4 w-4" />
            ) : (
              <Grid2x2 className="mr-2 h-4 w-4" />
            )}
            {cardsVisible ? 'Hide cards' : 'Show cards'}
          </DropdownMenuItem>
        )}
        {(onReset || onScreenshot || onBarReplay || onToggleCards) && <DropdownMenuSeparator />}

        {/* Section 2: panel/layout actions */}
        {onDuplicate && !locked && (
          <DropdownMenuItem onClick={onDuplicate}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
          </DropdownMenuItem>
        )}
        {onMove && !locked && (
          <DropdownMenuItem onClick={onMove}>
            <Move className="mr-2 h-4 w-4" />
            {moveLabel}
          </DropdownMenuItem>
        )}
        {onToggleHeaderVisible && (
          <DropdownMenuItem onClick={onToggleHeaderVisible}>
            {headerVisible ? (
              <PanelTopClose className="mr-2 h-4 w-4" />
            ) : (
              <PanelTop className="mr-2 h-4 w-4" />
            )}
            {headerVisible ? 'Hide top bar' : 'Show top bar'}
          </DropdownMenuItem>
        )}
        {(((onDuplicate || onMove) && !locked) || onToggleHeaderVisible) && (
          <DropdownMenuSeparator />
        )}

        {/* Section 3: general actions (Help is always present, so this
            section and the sections above always have a separator target) */}
        {onExport && (
          <DropdownMenuItem onClick={onExport}>
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </DropdownMenuItem>
        )}
        {onAskAI && (
          <DropdownMenuItem onClick={onAskAI}>
            <Sparkles className="mr-2 h-4 w-4" />
            Ask AI
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => console.log('[PanelMenu] Help clicked')}>
          <HelpCircle className="mr-2 h-4 w-4" />
          Help
        </DropdownMenuItem>

        {/* Section 4: destructive */}
        {!locked && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
